/**
 * <ReportScreenButton /> — Global floating action button (FAB) to report screen issues.
 *
 * Spec: CHAMADOS_VISIBILIDADE_SPEC §9.3.4 + §19.2 G-N4 (correção: usar Zustand
 * em vez de CustomEvent para evitar problema de listener múltiplo).
 *
 * Comportamento:
 *  - Renderiza um Affix bottom-right com ActionIcon laranja (IconBug)
 *  - Hotkey global: Ctrl+Shift+B (mod+shift+B no @mantine/hooks)
 *  - Esconde em rotas de auth: /login, /logout, /forgot-password, /reset-password
 *  - Esconde quando teclado virtual mobile abre (visualViewport.height < 75% innerHeight)
 *  - Quando modal já está aberto, fica invisível (opacity 0 + pointerEvents none)
 *
 * Acionamento: chama `useReportModalStore.getState().open()` (sem args quando
 * acionado pelo usuário; <ScreenErrorBoundary /> pode passar prefilled error).
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Affix, ActionIcon, Tooltip } from '@mantine/core'
import { useHotkeys, useMediaQuery } from '@mantine/hooks'
import { IconBug } from '@tabler/icons-react'
import { useReportModalStore } from '@/store/useReportModalStore'

/**
 * Afastamento do botao flutuante em relacao a borda direita da janela, no
 * desktop. Exportado para quem precisa reservar espaco embaixo dele.
 */
export const FAB_AFASTAMENTO_DIREITA = 24

/**
 * Lado do botao: `ActionIcon size="xl"` do Mantine. Medido no DOM de producao em
 * 20/09/2026 — 44 x 44. Se a variante mudar, este numero muda junto.
 */
export const FAB_LADO = 44

/**
 * Faixa que o botao ocupa a partir da borda DIREITA da janela.
 *
 * Existe porque ele tem `zIndex: 1000` e cobre, **sem aviso**, o que estiver
 * embaixo. Medido em producao em 20/09/2026, janela de 1920: o contador da
 * legenda do Diagrama MER ia de x=1599 a x=1867 e o botao ocupava 1852-1896 —
 * os ultimos 15 px do texto ficavam sob ele. Nao era corte por overflow
 * (`scrollWidth` == `clientWidth`, `overflow: visible`); era o botao por cima,
 * e um conserto de overflow nao teria mudado nada.
 *
 * Quem desenhar conteudo colado na borda inferior direita reserva isto.
 */
export const FAIXA_DO_BOTAO_FLUTUANTE = FAB_AFASTAMENTO_DIREITA + FAB_LADO

const HIDDEN_PATHS = ['/login', '/logout', '/forgot-password', '/reset-password']

export function ReportScreenButton() {
  const location = useLocation()
  const open = useReportModalStore((s) => s.open)
  const opened = useReportModalStore((s) => s.opened)
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const isMobile = useMediaQuery('(max-width: 768px)')

  // Hotkey global: Ctrl+Shift+B (Windows/Linux) / Cmd+Shift+B (Mac)
  useHotkeys([['mod+shift+B', () => open()]])

  // Detectar teclado virtual mobile via visualViewport API
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return
    const vv = window.visualViewport
    const handler = () => {
      // Heurística: se altura visual < 75% do innerHeight, assumimos teclado aberto
      setKeyboardOpen(vv.height < window.innerHeight * 0.75)
    }
    vv.addEventListener('resize', handler)
    return () => vv.removeEventListener('resize', handler)
  }, [])

  // Esconder em rotas de auth (uso de startsWith para cobrir query strings/sub-rotas)
  if (HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null
  if (keyboardOpen) return null

  // Mobile: subir o FAB para não cobrir conteúdo na borda inferior
  // Desktop: bottom 24 (padrão); AssistantWidget fica em right:80, este em right:24
  const fabBottom = isMobile ? 80 : 24
  const fabRight = isMobile ? 16 : FAB_AFASTAMENTO_DIREITA

  return (
    <Affix position={{ bottom: fabBottom, right: fabRight }} zIndex={1000}>
      <Tooltip label="Reportar problema nesta tela (Ctrl+Shift+B)" withArrow position="left">
        <ActionIcon
          size={isMobile ? 'lg' : 'xl'}
          radius="xl"
          color="orange"
          variant="filled"
          onClick={() => open()}
          aria-label="Reportar problema nesta tela"
          // Esconde temporariamente quando modal já está aberto (evita overlap visual)
          style={{
            opacity: opened ? 0 : 1,
            pointerEvents: opened ? 'none' : 'auto',
            transition: 'opacity 0.15s ease-in-out',
          }}
        >
          <IconBug size={isMobile ? 20 : 24} />
        </ActionIcon>
      </Tooltip>
    </Affix>
  )
}
